// 📍🔔 GEO-FENCE DEAL ALERTS — ping the POG'R when they wander near a live deal.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, BellOff, X } from "lucide-react";
import { playLockSound } from "@/lib/lockSound";

const RADIUS_DEFAULT = 0.25;
const COOLDOWN_MS = 6 * 3600 * 1000;
const CHECK_MS = 20000;

const radiusMi = () => {
  const v = parseFloat(localStorage.getItem("airpog_geofence_radius"));
  return [0.25, 0.5, 1].includes(v) ? v : RADIUS_DEFAULT;
};

const milesBetween = (a, b) => {
  const R = 3958.8, dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const seenMap = () => { try { return JSON.parse(localStorage.getItem("airpog_geofence_seen") || "{}"); } catch (e) { return {}; } };

export const GeoFenceAlerts = ({ userPos, businesses, onView }) => {
  const [alertBiz, setAlertBiz] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("airpog_voice_callouts") !== "0");
  const lastCheck = useRef(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), CHECK_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!userPos || !businesses?.length || alertBiz) return;
    if (localStorage.getItem("airpog_geofence_muted") === "1") return;
    const now = Date.now();
    if (now - lastCheck.current < CHECK_MS) return;
    lastCheck.current = now;
    const seen = seenMap();
    const rad = radiusMi();
    const fresh = (b) => b.lat && b.lng && (!seen[b.id] || now - seen[b.id] > COOLDOWN_MS) && milesBetween(userPos, b) <= rad;
    // 🎯 widened triggers: live deals first, then sales, then featured spots
    const hit = businesses.find((b) => b.has_deals && fresh(b))
      || businesses.find((b) => b.is_sale && fresh(b))
      || businesses.find((b) => b.featured && fresh(b));
    if (!hit) return;
    seen[hit.id] = now;
    localStorage.setItem("airpog_geofence_seen", JSON.stringify(seen));
    const dist = Math.round(milesBetween(userPos, hit) * 5280);
    const reason = hit.has_deals ? "has a live deal" : hit.is_sale ? "has a SALE on" : "is a featured POG spot";
    setAlertBiz({ ...hit, feet: dist, reason });
    playLockSound();
    // 🗣️ VOICE CALLOUT — drivers hear it without looking down (toggle: airpog_voice_callouts)
    try {
      if (localStorage.getItem("airpog_voice_callouts") !== "0" && typeof speechSynthesis !== "undefined") {
        try { speechSynthesis.cancel(); speechSynthesis.resume(); } catch (err) { /* ignore */ }
        const u = new SpeechSynthesisUtterance(`${hit.name}, about ${dist} feet ahead, ${reason}.`);
        u.rate = 1.02;
        speechSynthesis.speak(u);
      }
    } catch (e) { /* ignore */ }
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("📍 AIR.POG — worth a stop!", { body: `${hit.name} ${reason} ~${dist} ft away. Go POG it!` }); } catch (e) { /* ignore */ }
    }
  }, [userPos, businesses, alertBiz, tick]);

  const mute = () => { localStorage.setItem("airpog_geofence_muted", "1"); setAlertBiz(null); };
  const enableNotifs = () => { try { Notification?.requestPermission?.(); } catch (e) { /* ignore */ } };

  return (
    <AnimatePresence>
      {alertBiz && (
        <motion.div
          data-testid="geofence-alert"
          initial={{ opacity: 0, y: -24, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -24, scale: 0.95 }}
          className="absolute left-1/2 -translate-x-1/2 top-[calc(4.5rem+env(safe-area-inset-top))] z-[1700] w-[92vw] max-w-sm glass-strong border border-amber-400/50 rounded-2xl px-4 py-3 shadow-2xl"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-xl p-2 bg-amber-500/20 shrink-0"><MapPin className="w-5 h-5 text-amber-300" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-black text-sm text-amber-200 leading-tight">📍 YOU'RE NEAR A DEAL!</p>
              <p data-testid="geofence-alert-text" className="text-xs text-slate-200 mt-0.5">
                <span className="font-bold text-white">{alertBiz.name}</span> {alertBiz.reason || "has a live deal"} ~{alertBiz.feet} ft away 🔥
              </p>
              <div className="flex items-center gap-2 mt-2">
                <button data-testid="geofence-view-btn" onClick={() => { onView?.(alertBiz); setAlertBiz(null); }}
                  className="h-8 px-3.5 rounded-full bg-amber-400 text-slate-900 text-[11px] font-heading font-black active:scale-95 transition-transform">
                  SEE THE DEAL →
                </button>
                {typeof Notification !== "undefined" && Notification.permission === "default" && (
                  <button data-testid="geofence-enable-notifs" onClick={enableNotifs}
                    className="h-8 px-3 rounded-full bg-slate-800 border border-slate-600 text-slate-300 text-[10px] font-black">
                    🔔 ALERTS ON
                  </button>
                )}
                <button
                  data-testid="voice-callout-toggle"
                  title="Voice callouts on/off"
                  onClick={(e) => {
                    e.stopPropagation();
                    const nv = localStorage.getItem("airpog_voice_callouts") === "0";
                    localStorage.setItem("airpog_voice_callouts", nv ? "1" : "0");
                    if (!nv) { try { speechSynthesis.cancel(); } catch (err) { /* ignore */ } }
                    setVoiceOn(nv);
                  }}
                  className="h-8 w-8 rounded-full bg-slate-800 border border-slate-600 text-sm active:scale-95 transition-transform"
                >
                  {voiceOn ? "🔊" : "🔇"}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button data-testid="geofence-dismiss" onClick={() => setAlertBiz(null)}
                className="w-7 h-7 rounded-full bg-slate-800/80 flex items-center justify-center"><X className="w-3.5 h-3.5 text-slate-400" /></button>
              <button data-testid="geofence-mute" onClick={mute} title="Mute deal alerts"
                className="w-7 h-7 rounded-full bg-slate-800/80 flex items-center justify-center"><BellOff className="w-3.5 h-3.5 text-slate-500" /></button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
